import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { PhoneOff, Plus, Pencil, Trash2, Loader2, Undo2, Printer, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CallOutDialog } from "@/components/CallOut";
import { PeriodFilter } from "@/components/PeriodFilter";
import { periodLabelFor, periodIsoRange } from "@/lib/period";
import { printOrSavePdf } from "@/lib/printReport";
import { api } from "@/lib/api";

// Revert is only offered on the day of the call-out and the day after (you may have
// actually worked). After that it's history and the button disappears.
const canRevert = (dateStr) => {
  const m = String(dateStr || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return false;
  let y = +m[3];
  if (y < 100) y += 2000;
  const d = new Date(y, +m[1] - 1, +m[2]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  return diff === 0 || diff === 1;
};

export const CallOutsTab = () => {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const [editFmla, setEditFmla] = useState(false);
  const [fmlaTotal, setFmlaTotal] = useState("");
  const [period, setPeriod] = useState("year");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [visibleCount, setVisibleCount] = useState(40);
  const [reportOpen, setReportOpen] = useState(false);
  const [rpPeriod, setRpPeriod] = useState("year");
  const [rpFrom, setRpFrom] = useState("");
  const [rpTo, setRpTo] = useState("");
  const [reportHtml, setReportHtml] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const fmlaRef = useRef(null);

  // After an import, flash-focus the FMLA card so the updated balance is obvious.
  useEffect(() => {
    if (!banks?.fmla_tracked) return;
    if (localStorage.getItem("sd_focus_fmla") !== "1") return;
    localStorage.removeItem("sd_focus_fmla");
    const t = setTimeout(() => {
      const el = fmlaRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#E0575B]");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#E0575B]"), 2200);
    }, 350);
    return () => clearTimeout(t);
  }, [banks]);

  const load = useCallback(async () => {
    try {
      const [co, bk] = await Promise.all([api.listCallouts(), api.getCalloutBanks().catch(() => null)]);
      setItems(co); setBanks(bk);
    } catch { toast.error("Could not load call-outs"); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const allTime = items.reduce((s, i) => s + (i.points || 0), 0);
  const pointsInDays = (n) => {
    const cutoff = new Date(); cutoff.setHours(0, 0, 0, 0); cutoff.setDate(cutoff.getDate() - n);
    const sum = items.filter((i) => {
      if (!i.date_iso) return false;
      return new Date(i.date_iso + "T00:00:00") >= cutoff;
    }).reduce((s, i) => s + (i.points || 0), 0);
    return Math.round(sum * 100) / 100;
  };

  // Period filter scopes the list, the period points total, and the FMLA paid/unpaid
  // summary. ISO date strings sort lexicographically, so range checks are string compares.
  const periodLabel = periodLabelFor(period);
  const mdyIso = (mdy) => { const m = String(mdy || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/); if (!m) return ""; let y = +m[3]; if (y < 100) y += 2000; return `${y}-${String(+m[1]).padStart(2, "0")}-${String(+m[2]).padStart(2, "0")}`; };
  const isoOf = (i) => i.date_iso || mdyIso(i.date);
  const isoR = periodIsoRange(period, customFrom, customTo);
  const inRange = (iso) => !isoR ? true : (iso ? iso >= isoR[0] && iso <= isoR[1] : false);
  const filtered = items.filter((i) => inRange(isoOf(i))).sort((a, b) => (isoOf(b) || "").localeCompare(isoOf(a) || ""));
  const n2s = (v) => Math.round(v * 100) / 100;
  const periodPoints = n2s(filtered.reduce((s, i) => s + (i.points || 0), 0));
  // FMLA paid vs unpaid (period-scoped) — only FMLA call-outs count here.
  const isFmla = (i) => (i.type || "").startsWith("FMLA");
  const fmla = filtered.reduce((acc, i) => {
    if (!isFmla(i)) return acc;
    acc.paid += Number(i.hours_paid) || 0;
    acc.unpaid += Number(i.hours_unpaid) || 0;
    acc.hours += Number(i.fmla_hours) || 0;
    return acc;
  }, { paid: 0, unpaid: 0, hours: 0 });
  const hasFmlaEver = items.some(isFmla) || !!banks?.fmla_tracked;
  const timeOff = filtered.reduce((acc, i) => {
    if (i.type === "Vacation") { acc.vacHours += Number(i.shift_hours) || 0; acc.vacDays += 1; }
    else if (i.type === "ADO") { acc.adoDays += 1; }
    else if (i.type === "Trade") { acc.trades += 1; }
    else if (i.type === "Giveaway") { acc.giveaways += 1; }
    return acc;
  }, { vacHours: 0, vacDays: 0, adoDays: 0, trades: 0, giveaways: 0 });
  const presentTypes = [...new Set(filtered.map((i) => i.type).filter(Boolean))].sort();
  const listItems = typeFilter === "all" ? filtered : filtered.filter((i) => i.type === typeFilter);
  const visible = listItems.slice(0, visibleCount);

  const payLine = (i) => {
    if (i.type === "Late" || i.shift_hours == null) return null;
    // Early Release is fully paid by design — show it compactly.
    if (i.type === "Early Release") {
      return i.hours_paid > 0 ? `${i.hours_paid}h ${i.pay_type || "paid"}` : null;
    }
    const parts = [];
    if (i.pay_type && i.pay_type !== "Unpaid" && i.hours_paid > 0) parts.push(`${i.hours_paid}h ${i.pay_type}`);
    if (i.hours_unpaid > 0) parts.push(`${i.hours_unpaid}h Unpaid`);
    return `${i.shift_hours}h${parts.length ? ` · ${parts.join(" + ")}` : ""}${i.fmla_hours ? ` · ${i.fmla_hours}h FMLA` : ""}`;
  };

  const saveFmla = async () => {
    const t = Number(fmlaTotal);
    if (isNaN(t) || t < 0) { toast.error("Enter valid hours"); return; }
    try { setBanks(await api.saveCalloutBanks({ fmla_total: t })); setEditFmla(false); toast.success("FMLA total updated"); }
    catch { toast.error("Could not update FMLA total"); }
  };

  // Printable Time Off & FMLA report for the chosen date range.
  const buildReport = () => {
    const label = rpPeriod === "custom" && (rpFrom || rpTo)
      ? `${rpFrom || "…"} to ${rpTo || "…"}`
      : periodLabelFor(rpPeriod);
    const r = periodIsoRange(rpPeriod, rpFrom, rpTo);
    const mIso = (mdy) => { const m = String(mdy || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/); if (!m) return ""; let y = +m[3]; if (y < 100) y += 2000; return `${y}-${String(+m[1]).padStart(2, "0")}-${String(+m[2]).padStart(2, "0")}`; };
    const iOf = (i) => i.date_iso || mIso(i.date);
    const inR = (iso) => !r ? true : (iso ? iso >= r[0] && iso <= r[1] : false);
    const rows = items.filter((i) => inR(iOf(i))).sort((a, b) => (iOf(a) || "").localeCompare(iOf(b) || ""));
    const round = (v) => Math.round((v || 0) * 100) / 100;
    let fPaid = 0, fUnpaid = 0, fUsed = 0, vacDays = 0, vacHours = 0, adoDays = 0;
    let erVac = 0, erSick = 0;
    let trades = 0, giveaways = 0;
    rows.forEach((i) => {
      if ((i.type || "").startsWith("FMLA")) { fPaid += Number(i.hours_paid) || 0; fUnpaid += Number(i.hours_unpaid) || 0; fUsed += Number(i.fmla_hours) || 0; }
      else if (i.type === "Vacation") { vacDays += 1; vacHours += Number(i.shift_hours) || 0; }
      else if (i.type === "ADO") { adoDays += 1; }
      else if (i.type === "Trade") { trades += 1; }
      else if (i.type === "Giveaway") { giveaways += 1; }
      else if (i.type === "Early Release") { const h = Number(i.hours_paid) || 0; if (i.pay_type === "Sick") erSick += h; else erVac += h; }
    });
    const detailRows = rows.map((i) => {
      const paid = i.hours_paid != null ? `${round(i.hours_paid)}h` : "—";
      const unpaid = i.hours_unpaid != null ? `${round(i.hours_unpaid)}h` : "—";
      const fm = i.fmla_hours ? `${round(i.fmla_hours)}h` : "—";
      let typeCell = i.type || "";
      if (i.traded_with) typeCell += ` <span style="color:#666">(${i.type === "Trade" ? "with" : "to"} ${i.traded_with}${i.type === "Trade" && i.trade_date ? `, working ${i.trade_date}` : ""})</span>`;
      return `<tr><td>${i.date || ""}</td><td>${typeCell}</td><td>${paid}</td><td>${unpaid}</td><td>${fm}</td></tr>`;
    }).join("");
    const bankLine = banks?.fmla_tracked
      ? `<p class="sub">FMLA bank: ${banks.fmla_remaining} of ${banks.fmla_total} h remaining · ${banks.fmla_used}h used (rolling 12 months)</p>` : "";
    setReportHtml(`
      <style>#to-print-body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#111;max-width:720px;margin:auto}
      #to-print-body h1{font-size:22px;margin:0 0 2px}#to-print-body h2{font-size:14px;margin:24px 0 8px;color:#6b1d20;text-transform:uppercase;letter-spacing:.5px}
      #to-print-body .sub{color:#666;margin:0 0 12px}#to-print-body .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin:0 0 8px}
      #to-print-body .card{border:1px solid #e5e5e5;border-radius:10px;padding:12px}#to-print-body .card .n{font-size:22px;font-weight:800}#to-print-body .card .l{font-size:11px;color:#666;text-transform:uppercase}
      #to-print-body table{width:100%;border-collapse:collapse;font-size:13px}#to-print-body th,#to-print-body td{text-align:left;padding:7px 8px;border-bottom:1px solid #eee}#to-print-body th{color:#666;font-size:11px;text-transform:uppercase}
      #to-print-body td:nth-child(n+3),#to-print-body th:nth-child(n+3){text-align:right}#to-print-body .foot{margin-top:28px;font-size:11px;color:#999}
      #to-print-body .brand{display:flex;align-items:center;gap:12px;margin:0 0 14px}#to-print-body .brand img{width:46px;height:46px;border-radius:11px}#to-print-body .brand .bt{font-size:22px;font-weight:800;line-height:1.1}#to-print-body .brand .bs{font-size:12px;color:#6b1d20;font-weight:600;text-transform:uppercase;letter-spacing:.6px}</style>
      <div id="to-print-body">
      <div class="brand"><img src="/icon-512.png" alt="Stage Door"/><div><div class="bt">Stage Door</div><div class="bs">Time Off &amp; FMLA Report</div></div></div>
      <p class="sub">${label}</p>
      ${bankLine}
      <h2>FMLA hours</h2>
      <div class="grid">
        <div class="card"><div class="n">${round(fPaid)}h</div><div class="l">FMLA Paid</div></div>
        <div class="card"><div class="n">${round(fUnpaid)}h</div><div class="l">FMLA Unpaid</div></div>
        <div class="card"><div class="n">${round(fUsed)}h</div><div class="l">FMLA Counted</div></div>
      </div>
      <h2>Time off</h2>
      <div class="grid">
        <div class="card"><div class="n">${vacDays}</div><div class="l">Vacation Days</div></div>
        <div class="card"><div class="n">${round(vacHours)}h</div><div class="l">Vacation Hours</div></div>
        <div class="card"><div class="n">${adoDays}</div><div class="l">ADOs</div></div>
      </div>
      ${(trades + giveaways) > 0 ? `<h2>Shift coverage</h2>
      <div class="grid">
        <div class="card"><div class="n">${giveaways}</div><div class="l">Shifts Given Away</div></div>
        <div class="card"><div class="n">${trades}</div><div class="l">Shifts Traded</div></div>
        <div class="card"><div class="n">${trades + giveaways}</div><div class="l">Total Covered</div></div>
      </div>` : ""}
      ${(erVac + erSick) > 0 ? `<h2>Early release (paid)</h2>
      <div class="grid">
        <div class="card"><div class="n">${round(erVac)}h</div><div class="l">Vacation</div></div>
        <div class="card"><div class="n">${round(erSick)}h</div><div class="l">Sick</div></div>
        <div class="card"><div class="n">${round(erVac + erSick)}h</div><div class="l">Total ER</div></div>
      </div>` : ""}
      <h2>Detail</h2>
      <table><thead><tr><th>Date</th><th>Type</th><th>Paid</th><th>Unpaid</th><th>FMLA</th></tr></thead>
      <tbody>${detailRows || '<tr><td colspan="5">No entries in range</td></tr>'}</tbody></table>
      <p class="foot">Generated by Stage Door on ${new Date().toLocaleDateString()}. FMLA paid/unpaid reflect recorded hours on FMLA call-outs; vacation and ADO tracked separately.</p>
      </div>`);
    setReportOpen(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this call-out?")) return;
    try { await api.deleteCallout(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const revert = async (i) => {
    const what = i.revert_data?.kind === "shift" ? "shift" : "tour";
    if (!window.confirm(`Went to work after all? This will put the ${what} back on your schedule and remove this call-out.`)) return;
    try { await api.revertCallout(i.id); toast.success(`Reinstated the ${what} · call-out removed`); load(); window.dispatchEvent(new Event("schedule-invalidate")); }
    catch { toast.error("Could not revert"); }
  };

  return (
    <div className="space-y-4" data-testid="callouts-tab">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg font-700 text-zinc-100"><PhoneOff className="h-5 w-5 text-[#E0575B]" /> Time &amp; Attendance</h2>
        <div className="flex items-center gap-2">
          <Button onClick={() => setReportOpen(true)} variant="outline" className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800" data-testid="callouts-report-btn"><Printer className="mr-1.5 h-4 w-4" /> Report</Button>
          <Button onClick={() => setDlg({ open: true, initial: null })} className="bg-[#9B2226] text-white hover:bg-[#7f1c1f]" data-testid="callouts-add"><Plus className="mr-1.5 h-4 w-4" /> Add</Button>
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-sm border-zinc-800 bg-zinc-950" data-testid="callouts-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-50">Time Off &amp; FMLA Report</DialogTitle>
            <DialogDescription className="text-zinc-400">Pick a date range, then print or save as PDF.</DialogDescription>
          </DialogHeader>
          <PeriodFilter value={rpPeriod} onChange={setRpPeriod} from={rpFrom} to={rpTo} onFrom={setRpFrom} onTo={setRpTo} testid="report-period" />
          <Button onClick={buildReport} className="mt-2 w-full bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="report-generate"><Printer className="mr-1.5 h-4 w-4" /> Generate report</Button>
        </DialogContent>
      </Dialog>

      <PeriodFilter value={period} onChange={(k) => { setPeriod(k); setVisibleCount(40); }}
        from={customFrom} to={customTo} onFrom={setCustomFrom} onTo={setCustomTo} testid="callouts-period" />

      <div>
        <p className="mb-1.5 px-1 text-xs uppercase tracking-wide text-zinc-500" data-testid="callouts-window-label">Points by period</p>
        <div className="grid grid-cols-5 gap-2" data-testid="callouts-window-stats">
          {[30, 60, 90, 180, 365].map((d) => (
            <div key={d} className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-2.5 text-center" data-testid={`callouts-pts-${d}`}>
              <p className="font-display text-xl font-800 text-amber-300" data-testid={`callouts-pts-${d}-val`}>{pointsInDays(d)}</p>
              <p className="text-[10px] uppercase tracking-wide text-amber-300/60">{d}d</p>
            </div>
          ))}
        </div>
        <p className="mt-1.5 px-1 text-xs text-zinc-400" data-testid="callouts-alltime"><span className="font-600 text-zinc-200" data-testid="callouts-period-pts">{periodPoints} pts</span> in {periodLabel} · All time: <span className="font-600 text-zinc-200">{Math.round(allTime * 100) / 100} pts</span></p>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="callouts-timeoff-summary">
        <p className="mb-2 text-xs uppercase tracking-wide text-zinc-500">Time off · {periodLabel}</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-sky-500/25 bg-sky-500/10 p-2.5 text-center" data-testid="callouts-timeoff-vacation">
            <p className="font-display text-xl font-800 text-sky-300">{timeOff.vacDays}<span className="text-xs text-sky-300/60"> day{timeOff.vacDays === 1 ? "" : "s"}</span></p>
            <p className="text-[10px] uppercase tracking-wide text-sky-300/60">Vacation · {n2s(timeOff.vacHours)}h</p>
          </div>
          <div className="rounded-xl border border-violet-500/25 bg-violet-500/10 p-2.5 text-center" data-testid="callouts-timeoff-ado">
            <p className="font-display text-xl font-800 text-violet-300">{timeOff.adoDays}<span className="text-xs text-violet-300/60"> ADO{timeOff.adoDays === 1 ? "" : "s"}</span></p>
            <p className="text-[10px] uppercase tracking-wide text-violet-300/60">Authorized days off</p>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2" data-testid="callouts-coverage-summary">
          <div className="rounded-xl border border-teal-500/25 bg-teal-500/10 p-2.5 text-center" data-testid="callouts-coverage-giveaways">
            <p className="font-display text-xl font-800 text-teal-300">{timeOff.giveaways}<span className="text-xs text-teal-300/60"> given</span></p>
            <p className="text-[10px] uppercase tracking-wide text-teal-300/60">Shifts given away</p>
          </div>
          <div className="rounded-xl border border-teal-500/25 bg-teal-500/10 p-2.5 text-center" data-testid="callouts-coverage-trades">
            <p className="font-display text-xl font-800 text-teal-300">{timeOff.trades}<span className="text-xs text-teal-300/60"> traded</span></p>
            <p className="text-[10px] uppercase tracking-wide text-teal-300/60">Shifts traded</p>
          </div>
        </div>
      </div>

      {hasFmlaEver && (
        <div ref={fmlaRef} className="space-y-3 rounded-2xl border border-[#9B2226]/30 bg-[#9B2226]/10 p-4 transition-all" data-testid="callouts-fmla-bank">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-[#E0575B]">FMLA</p>
            {banks?.fmla_tracked && !editFmla && (
              <button onClick={() => { setFmlaTotal(String(banks.fmla_total)); setEditFmla(true); }} data-testid="fmla-edit" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200"><Pencil className="h-3 w-3" /> Edit total</button>
            )}
          </div>
          {banks?.fmla_tracked && (editFmla ? (
            <div className="flex items-center gap-2">
              <input type="number" step="0.01" value={fmlaTotal} onChange={(e) => setFmlaTotal(e.target.value)} className="w-28 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-100" data-testid="fmla-total-input" />
              <span className="text-xs text-zinc-400">total hours</span>
              <button onClick={saveFmla} className="rounded-md bg-[#9B2226] px-2.5 py-1 text-xs font-600 text-white hover:bg-[#7f1c1f]" data-testid="fmla-save">Save</button>
              <button onClick={() => setEditFmla(false)} className="text-xs text-zinc-400 hover:text-zinc-200" data-testid="fmla-cancel">Cancel</button>
            </div>
          ) : (
            <div>
              <p className="font-display text-3xl font-800 text-[#E0575B]" data-testid="fmla-remaining">{banks.fmla_remaining}<span className="text-base text-[#E0575B]/60"> / {banks.fmla_total} h left</span></p>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-800">
                <div className="h-full bg-[#E0575B] transition-[width]" style={{ width: `${banks.fmla_total > 0 ? Math.max(0, Math.min(100, (banks.fmla_remaining / banks.fmla_total) * 100)) : 0}%` }} />
              </div>
              <p className="mt-1 text-xs text-zinc-400">{banks.fmla_used}h used · rolling 12&nbsp;months</p>
            </div>
          ))}
          <div className="border-t border-[#9B2226]/20 pt-3">
            <p className="mb-2 text-[11px] uppercase tracking-wide text-[#E0575B]/70">FMLA hours: paid vs unpaid · {periodLabel}</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-2.5 text-center" data-testid="callouts-fmla-paid">
                <p className="font-display text-xl font-800 text-emerald-300">{n2s(fmla.paid)}<span className="text-xs text-emerald-300/60">h</span></p>
                <p className="text-[10px] uppercase tracking-wide text-emerald-300/60">FMLA paid</p>
              </div>
              <div className="rounded-xl border border-zinc-700/50 bg-zinc-800/40 p-2.5 text-center" data-testid="callouts-fmla-unpaid">
                <p className="font-display text-xl font-800 text-zinc-200">{n2s(fmla.unpaid)}<span className="text-xs text-zinc-400">h</span></p>
                <p className="text-[10px] uppercase tracking-wide text-zinc-400">FMLA unpaid</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {loaded && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-2" data-testid="callouts-list-header">
          <p className="text-xs uppercase tracking-wide text-zinc-500">Entries</p>
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setVisibleCount(40); }}>
            <SelectTrigger className="h-8 w-[160px] border-zinc-800 bg-zinc-900 text-xs focus:ring-[#E0575B]/70" data-testid="callouts-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-950">
              <SelectItem value="all" data-testid="callouts-type-all">All types</SelectItem>
              {presentTypes.map((t) => (
                <SelectItem key={t} value={t} data-testid={`callouts-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {loaded && filtered.length === 0 ? (
        <p className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-6 text-center text-sm text-zinc-400" data-testid="callouts-empty">{items.length === 0 ? "No call-outs recorded. Nice attendance! 🎉" : `No call-outs in ${periodLabel}.`}</p>
      ) : loaded && listItems.length === 0 ? (
        <p className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-6 text-center text-sm text-zinc-400" data-testid="callouts-empty-type">No {typeFilter} entries in {periodLabel}.</p>
      ) : (
        <div className="space-y-2">
          {visible.map((i) => (
            <div key={i.id} className="flex items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-900/40 px-3 py-2.5" data-testid={`callout-row-${i.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-500 text-zinc-100">{i.type}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-700 ${i.points > 0 ? "bg-amber-500/15 text-amber-300" : "bg-zinc-700/40 text-zinc-300"}`}>{i.points} pt</span>
                  {i.type === "Late" && i.minutes_late != null && <span className="text-xs text-zinc-400">{i.minutes_late} min late</span>}
                </div>
                <p className="mt-0.5 text-xs text-zinc-500">{i.date}{i.source_label ? ` · ${i.source_label}` : ""}{i.note ? ` · ${i.note}` : ""}</p>
                {i.traded_with && (
                  <p className="mt-0.5 text-xs text-teal-300/80" data-testid={`callout-traded-${i.id}`}>{i.type === "Trade" ? "Traded with" : "Gave to"} {i.traded_with}{i.type === "Trade" && i.trade_date ? ` · working ${i.trade_date}` : ""}</p>
                )}
                {payLine(i) && <p className="mt-0.5 text-xs text-emerald-300/80" data-testid={`callout-payline-${i.id}`}>{payLine(i)}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {i.revert_data && canRevert(i.date) && (
                  <button onClick={() => revert(i)} title="Went to work — put it back" className="inline-flex items-center gap-1 rounded-md border border-emerald-600/40 bg-emerald-500/10 px-2 py-1 text-[11px] font-600 text-emerald-300 transition-colors hover:bg-emerald-500/20" data-testid={`callout-revert-${i.id}`}><Undo2 className="h-3.5 w-3.5" /> Revert</button>
                )}
                <button onClick={() => setDlg({ open: true, initial: i })} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100" data-testid={`callout-edit-${i.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(i.id)} className="rounded-md p-1.5 text-red-400 hover:bg-zinc-800 hover:text-red-300" data-testid={`callout-del-${i.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {listItems.length > visibleCount && (
            <button onClick={() => setVisibleCount((c) => c + 40)} data-testid="callouts-show-more"
              className="w-full rounded-xl border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 text-sm font-600 text-zinc-300 transition-colors hover:bg-zinc-800">
              Show more ({listItems.length - visibleCount} older)
            </button>
          )}
        </div>
      )}

      <CallOutDialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} initial={dlg.initial} onSaved={load} />

      {reportHtml && (
        <div className="sd-print-area fixed inset-0 z-[120] overflow-auto bg-white" data-testid="report-print-overlay">
          <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-zinc-200 bg-white/95 px-4 py-3 backdrop-blur" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}>
            <button onClick={() => setReportHtml(null)} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 px-3 py-1.5 text-sm font-600 text-zinc-700 hover:bg-zinc-100" data-testid="report-print-close"><X className="h-4 w-4" /> Close</button>
            <button onClick={() => printOrSavePdf("to-print-body", "Stage Door - Time Off Report.pdf")} className="inline-flex items-center gap-1.5 rounded-full bg-[#9B2226] px-4 py-1.5 text-sm font-700 text-white hover:bg-[#7A1A1E]" data-testid="report-print-now"><Printer className="h-4 w-4" /> Print / Save PDF</button>
          </div>
          <div className="p-6" dangerouslySetInnerHTML={{ __html: reportHtml }} />
        </div>
      )}
    </div>
  );
};
